import { useState, useEffect } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Timer } from "lucide-react";
import { useEvent } from "@/components/EventLayout";
import { useI18n } from "@/context/I18nContext";

import { API } from '../../utils/apiBase';
const MIN_DURATION = 5;
const MAX_DURATION = 60;

export const CountdownSettingsCard = () => {
  const event = useEvent();
  const { t } = useI18n();
  const [settings, setSettings] = useState({
    countdown_mode: false,
    circle_position_order: "4to1",
    countdown_sound_enabled: false,
    battle_duration_seconds: 30,
    circle_duration_seconds: 30,
    circle_rounds: 3,
  });
  const [battleDurInput, setBattleDurInput] = useState("30");
  const [circleDurInput, setCircleDurInput] = useState("30");

  useEffect(() => {
    if (!event?.event_id) return;
    axios.get(`${API}/event/${event.event_id}/countdown-settings`).then(res => {
      setSettings(res.data);
      setBattleDurInput(String(res.data.battle_duration_seconds || 30));
      setCircleDurInput(String(res.data.circle_duration_seconds || 30));
    }).catch(() => {});
  }, [event?.event_id]);

  const update = async (patch) => {
    if (!event?.event_id) return;
    try {
      const res = await axios.put(`${API}/event/${event.event_id}/countdown-settings`, patch);
      setSettings(res.data);
      toast.success(t('timer.settingsUpdated'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('common.error'));
    }
  };

  const commitDuration = (field, raw, setLocal) => {
    const n = parseInt(raw, 10);
    if (Number.isNaN(n) || n < MIN_DURATION || n > MAX_DURATION) {
      toast.error(t('timer.durationOutOfRange').replace('{min}', MIN_DURATION).replace('{max}', MAX_DURATION));
      setLocal(String(settings[field] || 30));
      return;
    }
    if (n === settings[field]) return;
    update({ [field]: n });
  };

  return (
    <Card className="p-6 mb-6 glass" data-testid="countdown-settings-card">
      <div className="flex items-center gap-3 mb-5">
        <Timer className="text-primary" size={28} />
        <h3 className="text-3xl font-bold text-foreground">{t('timer.modeTitle')}</h3>
      </div>

      {/* Toggle */}
      <div className="flex items-center justify-between mb-4 p-4 bg-muted rounded-lg">
        <div>
          <div className="font-bold text-foreground text-lg">{t('timer.modeToggleTitle')}</div>
          <div className="text-sm text-muted-foreground mt-1">{t('timer.modeToggleDesc')}</div>
        </div>
        <Switch
          checked={settings.countdown_mode}
          onCheckedChange={(v) => update({ countdown_mode: !!v })}
          data-testid="countdown-mode-switch"
        />
      </div>

      {/* Position order */}
      <div className="flex items-center justify-between mb-4 p-4 bg-muted rounded-lg">
        <div>
          <div className="font-bold text-foreground text-lg">{t('timer.positionOrder')}</div>
          <div className="text-sm text-muted-foreground mt-1">{t('timer.positionOrderDesc')}</div>
        </div>
        <div className="flex gap-2">
          <Button
            variant={settings.circle_position_order === "4to1" ? "default" : "outline"}
            onClick={() => update({ circle_position_order: "4to1" })}
            data-testid="position-order-4to1"
          >
            4 → 3 → 2 → 1
          </Button>
          <Button
            variant={settings.circle_position_order === "1to4" ? "default" : "outline"}
            onClick={() => update({ circle_position_order: "1to4" })}
            data-testid="position-order-1to4"
          >
            1 → 2 → 3 → 4
          </Button>
        </div>
      </div>

      {/* Circle pre-countdown 3-2-1 toggle */}
      <div className="flex items-center justify-between mb-4 p-4 bg-muted rounded-lg">
        <div>
          <div className="font-bold text-foreground text-lg">{t('timer.circlePreCountdownTitle')}</div>
          <div className="text-sm text-muted-foreground mt-1">{t('timer.circlePreCountdownDesc')}</div>
        </div>
        <Switch
          checked={settings.circle_pre_countdown_enabled !== false}
          onCheckedChange={(v) => update({ circle_pre_countdown_enabled: !!v })}
          data-testid="circle-pre-countdown-switch"
        />
      </div>

      {/* Sound toggle */}
      <div className="flex items-center justify-between mb-4 p-4 bg-muted rounded-lg">
        <div>
          <div className="font-bold text-foreground text-lg">{t('timer.soundTitle')}</div>
          <div className="text-sm text-muted-foreground mt-1">{t('timer.soundDesc')}</div>
        </div>
        <Switch
          checked={settings.countdown_sound_enabled}
          onCheckedChange={(v) => update({ countdown_sound_enabled: !!v })}
          data-testid="countdown-sound-switch"
        />
      </div>

      {/* Battle duration */}
      <div className="flex items-center justify-between mb-4 p-4 bg-muted rounded-lg">
        <div className="flex-1 mr-4">
          <div className="font-bold text-foreground text-lg">{t('timer.battleDurationTitle')}</div>
          <div className="text-sm text-muted-foreground mt-1">
            {t('timer.battleDurationDesc').replace('{min}', MIN_DURATION).replace('{max}', MAX_DURATION)}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Input
            type="number"
            min={MIN_DURATION}
            max={MAX_DURATION}
            value={battleDurInput}
            onChange={(e) => setBattleDurInput(e.target.value)}
            onBlur={() => commitDuration('battle_duration_seconds', battleDurInput, setBattleDurInput)}
            onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
            className="w-20 text-center text-lg font-bold"
            data-testid="battle-duration-input"
          />
          <span className="text-muted-foreground text-sm">{t('timer.seconds')}</span>
        </div>
      </div>

      {/* Circle duration */}
      <div className="flex items-center justify-between mb-4 p-4 bg-muted rounded-lg">
        <div className="flex-1 mr-4">
          <div className="font-bold text-foreground text-lg">{t('timer.circleDurationTitle')}</div>
          <div className="text-sm text-muted-foreground mt-1">
            {t('timer.circleDurationDesc').replace('{min}', MIN_DURATION).replace('{max}', MAX_DURATION)}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Input
            type="number"
            min={MIN_DURATION}
            max={MAX_DURATION}
            value={circleDurInput}
            onChange={(e) => setCircleDurInput(e.target.value)}
            onBlur={() => commitDuration('circle_duration_seconds', circleDurInput, setCircleDurInput)}
            onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
            className="w-20 text-center text-lg font-bold"
            data-testid="circle-duration-input"
          />
          <span className="text-muted-foreground text-sm">{t('timer.seconds')}</span>
        </div>
      </div>

      {/* Circle rounds */}
      <div className="flex items-center justify-between mb-4 p-4 bg-muted rounded-lg">
        <div>
          <div className="font-bold text-foreground text-lg">{t('timer.roundsTitle')}</div>
          <div className="text-sm text-muted-foreground mt-1">{t('timer.roundsDesc')}</div>
        </div>
        <div className="flex gap-2">
          {[2, 3].map((r) => (
            <Button
              key={r}
              variant={settings.circle_rounds === r ? "default" : "outline"}
              onClick={() => update({ circle_rounds: r })}
              data-testid={`circle-rounds-${r}`}
            >
              {r} {t('timer.rounds')}
            </Button>
          ))}
        </div>
      </div>

      {/* Keyboard shortcuts moved to dedicated card below */}
    </Card>
  );
};

export default CountdownSettingsCard;
